import { cn } from '@/lib/utils';
import { ScorecardRichText } from '@/components/scorecard/ScorecardRichText';

export type CommercialityBlockStructured = {
  overall_feedback?: string;
  detailed_feedback?: string;
};

export type CommercialityStructured = {
  commercial_viability_pct?: number | null;
  originality_pct?: number | null;
  target_market_pct?: number | null;
  originality?: CommercialityBlockStructured;
  target_market?: CommercialityBlockStructured;
  commercial_viability?: CommercialityBlockStructured;
  /** Legacy — ignored; Genre Promise lives under Engagement Theme. */
  genre_promise?: CommercialityBlockStructured;
  genre_promise_pct?: number | null;
};

type CommercialitySectionProps = {
  structured?: CommercialityStructured | null;
};

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${Math.round(v)}%`;
}

function Block({
  title,
  score,
  block,
}: {
  title: string;
  score: string;
  block?: CommercialityBlockStructured;
}) {
  return (
    <div className="mb-8 pb-6 border-b border-[#2a2a2a] last:border-0">
      <div className="flex flex-wrap items-baseline gap-3 mb-4">
        <h4 className="text-base font-semibold text-white">{title}</h4>
        <span className="text-sm text-white/60">Score: {score}</span>
      </div>
      {block?.overall_feedback?.trim() ? (
        <div className="mb-4">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/45 mb-2">
            Overall User Feedback
          </p>
          <ScorecardRichText text={block.overall_feedback} />
        </div>
      ) : null}
      {block?.detailed_feedback?.trim() ? (
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/45 mb-2">
            Detailed User Feedback
          </p>
          <ScorecardRichText text={block.detailed_feedback} />
        </div>
      ) : null}
    </div>
  );
}

export function CommercialitySection({ structured }: CommercialitySectionProps) {
  if (
    !structured?.originality &&
    !structured?.target_market &&
    !structured?.commercial_viability
  ) {
    return null;
  }

  const origScore =
    structured.originality_pct != null ? pct(structured.originality_pct) : 'not applicable';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-4 mb-6 text-sm text-white/70">
        <span>
          Commercial Viability:{' '}
          <span className={cn('font-semibold text-white')}>
            {pct(structured.commercial_viability_pct)}
          </span>
        </span>
        <span>Originality: {origScore}</span>
        <span>Target Market: {pct(structured.target_market_pct)}</span>
      </div>

      <div className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-[#3a3a3a]">
          Originality
        </h3>
        <Block title="Originality Metre" score={origScore} block={structured.originality} />
      </div>

      <div className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-[#3a3a3a]">
          Target Market
        </h3>
        <Block
          title="Target Market"
          score={pct(structured.target_market_pct)}
          block={structured.target_market}
        />
      </div>

      <div className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-4 pb-2 border-b border-[#3a3a3a]">
          Commercial Viability
        </h3>
        <Block
          title="Commercial Viability"
          score={pct(structured.commercial_viability_pct)}
          block={structured.commercial_viability}
        />
      </div>
    </div>
  );
}

export default CommercialitySection;
